import {Inject, Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {DobryAgentAuthor, DobryAgentBlogTag} from "../interface/dobry-agent-blog-article";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {FilterService} from "./filter.service";
import {DOCUMENT} from "@angular/common";
import {catchError, first, map, switchMap, take, tap} from "rxjs/operators";
import {Uuid} from "../interface/response/uuid";
import {ListResponse} from "../interface/response/list-response";
import {ConfirmDialogComponent} from "../components/confirm-dialog/confirm-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class DobryAgentAuthorService {
  public readonly authorListSubject: ReplaySubject<CountedAndPaginated<DobryAgentAuthor>>
    = new ReplaySubject<CountedAndPaginated<DobryAgentAuthor>>(1);
  public readonly authorNotCountedListSubject: ReplaySubject<ListResponse<DobryAgentAuthor>> = new ReplaySubject<ListResponse<DobryAgentAuthor>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
    private snack: MatSnackBar,
    private dialog: MatDialog,
  ) {
  }

  public loadList(page: number, pageSize: number, filters: object, sort?: { sortBy?: string; sortOrder?: string}): void {
    this.httpClient.get(`/rest/dobryagent/api/internal/author?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<DobryAgentAuthor>) => {
        this.authorListSubject.next(result);
      });
  }

  public loadNotCountedList(filters: object): void {
    this.httpClient
      .get(`/rest/dobryagent/api/internal/author?disableCount=1&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<DobryAgentAuthor>) => {
        this.authorNotCountedListSubject.next(result);
      });
  }

  public getAuthorListSubject(): ReplaySubject<CountedAndPaginated<DobryAgentAuthor>> {
    return this.authorListSubject;
  }

  public getAuthorNotCountedListSubject(): ReplaySubject<ListResponse<DobryAgentAuthor>> {
    return this.authorNotCountedListSubject;
  }

  public get(uuid: string): Observable<DobryAgentAuthor> {
    return this.httpClient
      .get<DobryAgentAuthor>(`/rest/dobryagent/api/internal/author/${uuid}`)
      .pipe(take(1));
  }

  public send(formData: object, uuid?: string): Observable<Uuid> {
    if (uuid) {
      return this.httpClient
        .put<Uuid>(`/rest/dobryagent/api/internal/author/${uuid}`, formData)
        .pipe(take(1));
    }

    return this.httpClient
      .post<Uuid>(`/rest/dobryagent/api/internal/author`, formData)
      .pipe(take(1));
  }

  public uploadImage(file: File, authorUuid: string): Observable<any> {
    const formData = new FormData();
    formData.append('content', file);

    return this.httpClient
      .post(`/rest/dobryagent/api/internal/author/${authorUuid}/image-upload`, formData)
      .pipe(take(1));
  }

  public deleteImage(authorUuid: string): Observable<void> {
    return this.httpClient
      .delete<void>(
        `/rest/dobryagent/api/internal/author/${authorUuid}/image-delete`
      )
      .pipe(take(1));
  }

  private delete(uuid: string): Observable<void> {
    return this.httpClient
      .delete<void>(
        `/rest/dobryagent/api/internal/author/${uuid}/delete`
      )
      .pipe(first());
  }

  private deleteWeak(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/author/${uuid}`,
        {}
      )
      .pipe(first());
  }


  private restore(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/author/${uuid}/restore`,
        {}
      )
      .pipe(first());
  }

  public confirmDelete(
    author: DobryAgentAuthor
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz usunąć autora?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),

        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.delete(author.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie usunięto autora',
                  '',
                  {duration: 2000}
                );
              }),

              map(() => true),

              catchError((error: HttpErrorResponse) => {
                const message =
                  error.error?.message
                  ?? 'Nie można usunąć autora';

                this.snack.open(
                  message,
                  'Zamknij',
                  {duration: 5000}
                );

                return of(false);
              })
            );
        })
      );
  }

  public confirmDeleteWeak(
    uuid: string
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przenieść autora do archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.deleteWeak(uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przeniesiono autora do archiwum',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }


  public confirmRestore(
    blogTag: DobryAgentAuthor
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przywrócić autora z archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.restore(blogTag.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przywrócono autora',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }
}
